import * as React from 'react';
import {
  DefaultButton,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  PrimaryButton,
  Tooltip,
  Typography,
  makeStyles,
} from '@bb-ui/react-library';
import { DialogTitle } from '@bb-ui/react-library/dist/components/DialogTitle';
import { TextField } from '@bb-ui/react-library/dist/components/TextField';
import { useTranslation } from 'react-i18next';
import useRestApi from 'hooks/useRestApi';
import { apiUrl } from 'utils/apiUrl';
import { ArtifactApprovalType, ArtifactType, IArtifactData } from 'App.types';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { artifactStageFor } from 'utils/artifactStage';
import { compareLearnVersions, isOlderLearnVersion } from 'utils/learnVersion';

const RESULTS_COUNT = 500;
const APPROVAL_STATUS: ArtifactApprovalType = 'yes';
const ARTIFACT_TYPE: ArtifactType = 'learn-installer';
const VERSION_ITEM_HEIGHT = 36;
const VISIBLE_VERSIONS = 8;

const useStyles = makeStyles((theme) => ({
  versionField: {
    marginTop: theme.spacing(2),
  },
  olderWarning: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
}));

const getVersion = (artifact: IArtifactData): string =>
  artifact.artifact_basename
    ? artifact.artifact_basename.replace(/\.zip$/, '')
    : artifact.artifact_key.split('/')[2].replace(/\.zip$/, '');

export interface ReleaseStageTargetVersionDialogProps {
  id: string;
  open: boolean;
  currentVersion: string;
  lastVersion?: string;
  submitting?: boolean;
  onConfirm: (version: string) => void;
  onClose: () => void;
}

export const ReleaseStageTargetVersionDialog: React.FC<ReleaseStageTargetVersionDialogProps> = ({
  id,
  open,
  currentVersion,
  lastVersion,
  submitting,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const appConfig = useAppConfigContext();
  const [version, setVersion] = React.useState(currentVersion);

  React.useEffect(() => {
    if (open) setVersion(currentVersion);
  }, [open, currentVersion]);

  const searchQueryParams = new URLSearchParams({
    artifact_name: `${ARTIFACT_TYPE}/${artifactStageFor(appConfig.stage)}`,
    results_count: RESULTS_COUNT.toString(),
    approval: APPROVAL_STATUS,
  });

  const { data, loading } = useRestApi<IArtifactData[]>(
    apiUrl('fcc', `artifacts?${searchQueryParams.toString()}`),
  );

  const versions = React.useMemo(() => {
    const available = (data ?? []).map(getVersion);
    const all = available.includes(currentVersion) ? available : [currentVersion, ...available];
    return [...all].sort((left, right) => compareLearnVersions(right, left));
  }, [data, currentVersion]);

  const floorVersion = lastVersion || currentVersion;

  const goesBackwards = Boolean(
    version && floorVersion && isOlderLearnVersion(version, floorVersion),
  );

  const blockedByLast = goesBackwards && Boolean(lastVersion);

  const confirmDisabledReason = React.useMemo(() => {
    if (submitting) return t('releaseStageMembers.editTargetVersion.submittingReason');
    if (!version) return t('releaseStageMembers.editTargetVersion.noVersionReason');
    if (version === currentVersion)
      return t('releaseStageMembers.editTargetVersion.sameVersionReason');
    return '';
  }, [currentVersion, submitting, t, version]);

  return (
    <Dialog id={id} open={open} onClose={onClose} aria-labelledby={`${id}-title`} data-testid={id}>
      <DialogTitle id={`${id}-title`} onClose={onClose} closeLabel={t('global.close')}>
        {t('releaseStageMembers.editTargetVersion.title')}
      </DialogTitle>
      <DialogContent>
        <TextField
          select
          fullWidth
          floatingLabel
          className={classes.versionField}
          id={`${id}-version`}
          data-testid={`${id}-version`}
          label={t('releaseStageMembers.editTargetVersion.label')}
          value={version}
          disabled={loading || submitting}
          SelectProps={{
            MenuProps: {
              variant: 'menu',
              PaperProps: {
                style: { maxHeight: VISIBLE_VERSIONS * VERSION_ITEM_HEIGHT },
              },
            },
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setVersion(event.target.value)}
        >
          {versions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
        {goesBackwards && (
          <Typography
            variant="body2"
            className={classes.olderWarning}
            data-testid={`${id}-older-warning`}
          >
            {t(
              blockedByLast
                ? 'releaseStageMembers.editTargetVersion.belowLast'
                : 'releaseStageMembers.editTargetVersion.olderThanTarget',
              { version: floorVersion },
            )}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Tooltip title={confirmDisabledReason}>
          <span>
            <PrimaryButton
              disabled={confirmDisabledReason !== '' || blockedByLast}
              onClick={() => onConfirm(version)}
              data-testid={`${id}-confirm`}
            >
              {t('releaseStageMembers.editTargetVersion.confirm')}
            </PrimaryButton>
          </span>
        </Tooltip>
        <DefaultButton disabled={submitting} onClick={onClose}>
          {t('global.cancel')}
        </DefaultButton>
      </DialogActions>
    </Dialog>
  );
};
